import { Injectable } from '@angular/core';
import { HttpClient, HttpContext, HttpHeaders } from '@angular/common/http';
import { catchError, forkJoin, from, map, Observable, of, switchMap, tap, throwError } from 'rxjs';
import { MsalService } from '@azure/msal-angular';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { Constant } from '../shared/constants';
import * as dynamicCRMModels from '../models/dynamic-crm-models';
import { SKIP_GLOBAL_LOADER } from '../shared/loader/loaderInterceptor';

export interface ICrmEntityResolution {
  partyId: string;
  partyType: string;
  crmId: string | null;
  crmEntityType: 'contact' | 'account' | null;
}

@Injectable({
  providedIn: 'root'
})
export class DynamicCrmService {

  private readonly CRM_TOKEN_KEY = 'crmAccessToken';
  private readonly skipGlobalLoaderContext = new HttpContext().set(SKIP_GLOBAL_LOADER, true);
  private resolutionCache = new Map<string, ICrmEntityResolution>();

  constructor(
    private _httpClient: HttpClient,
    private msalService: MsalService
  ) {}

  // ── Token acquisition ──────────────────────────────────────────────────────

  private getCrmAccessToken(): Observable<string> {
    const cached = localStorage.getItem(this.CRM_TOKEN_KEY);
    if (cached && !this.isTokenExpired(cached)) {
      return of(cached);
    }

    const accounts = this.msalService.instance.getAllAccounts();
    if (accounts.length && !this.msalService.instance.getActiveAccount()) {
      this.msalService.instance.setActiveAccount(accounts[0]);
    }

    const scope = Constant.uri.getCrmAccessToken;
    return from(
      this.msalService.instance
        .acquireTokenSilent({ scopes: [scope] })
        .catch((err: unknown) => {
          if (err instanceof InteractionRequiredAuthError) {
            return this.msalService.instance.acquireTokenPopup({ scopes: [scope] });
          }
          throw err;
        })
    ).pipe(
      map(result => {
        localStorage.setItem(this.CRM_TOKEN_KEY, result.accessToken);
        return result.accessToken;
      })
    );
  }

  private isTokenExpired(token: string): boolean {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return Math.floor(Date.now() / 1000) > payload['exp'];
    } catch {
      return true;
    }
  }

  private getCrmHttpOptions(): Observable<{ headers: HttpHeaders; context: HttpContext }> {
    return this.getCrmAccessToken().pipe(
      map(token => ({
        headers: new HttpHeaders({
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Prefer': 'odata.include-annotations="OData.Community.Display.V1.FormattedValue",return=representation'
        }),
        context: this.skipGlobalLoaderContext
      }))
    );
  }

  private getCrmReadHttpOptions(): Observable<{ headers: HttpHeaders; context: HttpContext }> {
    return this.getCrmAccessToken().pipe(
      map(token => ({
        headers: new HttpHeaders({
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json, text/plain, */*',
          'Prefer': 'odata.include-annotations="OData.Community.Display.V1.FormattedValue"'
        }),
        context: this.skipGlobalLoaderContext
      }))
    );
  }

  private getCrmBinaryHttpOptions(contentType = 'application/octet-stream'): Observable<{ headers: HttpHeaders; context: HttpContext }> {
    return this.getCrmAccessToken().pipe(
      map(token => ({
        headers: new HttpHeaders({
          'Authorization': `Bearer ${token}`,
          'Content-Type': contentType,
          'Accept': 'application/json, text/plain, */*',
          'OData-Version': '4.0',
          'OData-MaxVersion': '4.0'
        }),
        context: this.skipGlobalLoaderContext
      }))
    );
  }

  // ── Entity resolution ──────────────────────────────────────────────────────

  /**
   * Resolves a D365 contactid or accountid from an internal partyId + partyType.
   * Results are cached in-memory for the lifetime of the service instance.
   */
  resolveCrmEntityByParty(partyId: string, partyType: string): Observable<ICrmEntityResolution> {
    const cacheKey = `${partyId}__${partyType}`;
    const cached = this.resolutionCache.get(cacheKey);
    if (cached) return of(cached);

    const normalizedType = (partyType || '').trim().toLowerCase();
    const isOrg = normalizedType === 'organization';

    const baseUrl = isOrg
      ? `${Constant.uri.getCrmAccounts}?$select=accountid&$filter=rcm_rockcodbpartyid eq '${encodeURIComponent(partyId)}'&$top=1`
      : `${Constant.uri.getCrmContacts}?$select=contactid&$filter=rcm_rockcodbpartyid eq '${encodeURIComponent(partyId)}'&$top=1`;

    return this.getCrmHttpOptions().pipe(
      switchMap(options =>
        this._httpClient.get<{ value: any[] }>(baseUrl, options).pipe(
          map(response => {
            const item = response?.value?.[0];
            const crmId: string | null = item ? (isOrg ? item['accountid'] : item['contactid']) : null;
            const result: ICrmEntityResolution = {
              partyId,
              partyType,
              crmId: crmId ?? null,
              crmEntityType: crmId ? (isOrg ? 'account' : 'contact') : null
            };
            this.resolutionCache.set(cacheKey, result);
            return result;
          }),
          catchError(() => of({ partyId, partyType, crmId: null, crmEntityType: null } as ICrmEntityResolution))
        )
      )
    );
  }

  // ── Get Notes ──────────────────────────────────────────────────────────────

  /**
   * Fetches D365 annotations linked to any of the provided parties.
   * Resolves all CRM entities in parallel, then queries /annotations.
   */
  getNotes(
    partyDetails: { partyId: string; partyType: string }[],
    sortOrder: 'asc' | 'desc' = 'desc',
    searchText?: string
  ): Observable<dynamicCRMModels.ICrmNote[]> {
    if (!partyDetails.length) return of([]);

    const unique = partyDetails.filter(
      (p, i, arr) => arr.findIndex(x => x.partyId === p.partyId) === i
    );

    const resolutions$ = unique.map(p => this.resolveCrmEntityByParty(p.partyId, p.partyType));

    return forkJoin(resolutions$).pipe(
      switchMap(resolutions => {
        const ids = resolutions
          .filter(r => r.crmId != null)
          .map(r => r.crmId as string);

        if (!ids.length) return of([] as dynamicCRMModels.ICrmNote[]);

        const idFilter = ids.map(id => `(_objectid_value eq ${id})`).join(' or ');
        let filter = `(${idFilter})`;

        if (searchText?.trim()) {
          const safe = searchText.trim().replace(/'/g, "''");
          filter += ` and (contains(subject,'${safe}') or contains(notetext,'${safe}'))`;
        }

        const select = 'annotationid,subject,notetext,mimetype,filename,createdon,isdocument,_ownerid_value,_objectid_value';
        const expand = 'objectid_contact($select=fullname),objectid_account($select=name)';
        const url = `${Constant.config.dynamicCrmBaseUrl}api/data/v9.2/annotations?$select=${select}&$expand=${expand}&$filter=${filter}&$orderby=createdon ${sortOrder}&$top=100`;

        return this.getCrmHttpOptions().pipe(
          switchMap(options =>
            this._httpClient.get<{ value: any[] }>(url, options).pipe(
              map(response => (response?.value || []).map(item => this.mapToICrmNote(item))),
              catchError(() => of([] as dynamicCRMModels.ICrmNote[]))
            )
          )
        );
      })
    );
  }

  private mapToICrmNote(item: any): dynamicCRMModels.ICrmNote {
    return {
      annotationId: item['annotationid'],
      subject: item['subject'] || '',
      notetext: item['notetext'] || '',
      mimetype: item['mimetype'],
      filename: item['filename'],
      createdon: item['createdon'],
      ownerName: item['_ownerid_value@OData.Community.Display.V1.FormattedValue'] || '',
      contactFullName: item['objectid_contact']?.['fullname'] || '',
      accountName: item['objectid_account']?.['name'] || '',
      isExpanded: false
    };
  }

  getAttachmentPayload(annotationId: string): Observable<dynamicCRMModels.IAttachmentPayload> {
    if (!annotationId) {
      return of({});
    }

    const normalizedAnnotationId = annotationId.replace(/[{}]/g, '');
    const url = `${Constant.config.dynamicCrmBaseUrl}api/data/v9.2/annotations(${normalizedAnnotationId})?$select=documentbody,mimetype,filename`;

    return this.getCrmHttpOptions().pipe(
      switchMap(options => this._httpClient.get<any>(url, options)),
      map(response => ({
        documentbody: response?.documentbody,
        mimetype: response?.mimetype,
        filename: response?.filename
      })),
      catchError(() => of({}))
    );
  }

  getAttachmentDownloadData(annotationId: string): Observable<dynamicCRMModels.IAttachmentDownloadData | null> {
    return this.getAttachmentPayload(annotationId).pipe(
      map(attachment => {
        if (!attachment?.documentbody || !attachment?.mimetype || !attachment?.filename) {
          return null;
        }

        const binary = atob(attachment.documentbody);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) {
          bytes[i] = binary.charCodeAt(i);
        }

        return {
          filename: attachment.filename,
          mimetype: attachment.mimetype,
          bytes: bytes.buffer as ArrayBuffer
        };
      }),
      catchError(() => of(null))
    );
  }

  // ── Add Note ───────────────────────────────────────────────────────────────

  /**
   * Creates a D365 annotation (note) linked to the resolved CRM contact or account.
   * Resolves the CRM entity first, then POSTs to /annotations.
   */
  addNote(partyId: string, partyType: string, note: { title: string; body: string; filename?: string; mimetype?: string; documentbody?: string }): Observable<any> {
    return this.resolveCrmEntityByParty(partyId, partyType).pipe(
      switchMap(resolved => {
        if (!resolved.crmId || !resolved.crmEntityType) {
          throw new Error(`Could not resolve CRM entity for partyId=${partyId}, partyType=${partyType}`);
        }

        const entitySet = resolved.crmEntityType === 'account' ? 'accounts' : 'contacts';
        const isAccount = resolved.crmEntityType === 'account';
        const bindValue = `/${entitySet}(${resolved.crmId})`;

        const payload: dynamicCRMModels.IAddNotePayload = {
          subject: note.title,
          notetext: note.body,
          ...(isAccount
            ? { 'objectid_account@odata.bind': bindValue }
            : { 'objectid_contact@odata.bind': bindValue })
        };

        if (note.documentbody && note.filename && note.mimetype) {
          payload.filename = note.filename;
          payload.mimetype = note.mimetype;
          payload.documentbody = note.documentbody;
          payload.isdocument = true;
        }

        const annotationsUrl = `${Constant.config.dynamicCrmBaseUrl}api/data/v9.2/annotations`;
        console.debug('[DynamicCrmService] addNote request', {
          partyId,
          partyType,
          resolvedCrmEntityType: resolved.crmEntityType,
          resolvedCrmId: resolved.crmId,
          hasAttachment: !!(note.documentbody && note.filename && note.mimetype),
          subject: payload.subject,
          bodyLength: payload.notetext?.length || 0
        });

        return this.getCrmHttpOptions().pipe(
          switchMap(options => this._httpClient.post(annotationsUrl, payload, options)),
          tap(response => {
            console.debug('[DynamicCrmService] addNote success', {
              partyId,
              partyType,
              response
            });
          }),
          catchError(error => {
            console.error('[DynamicCrmService] addNote failed', {
              partyId,
              partyType,
              resolvedCrmEntityType: resolved.crmEntityType,
              resolvedCrmId: resolved.crmId,
              error
            });
            return throwError(() => error);
          })
        );
      })
    );
  }

  // ── Tasks ─────────────────────────────────────────────────────────────────

  getTasksByProfiles(params: dynamicCRMModels.ITaskQueryParams): Observable<dynamicCRMModels.ICrmTask[]> {
    const uniqueIds = Array.from(new Set((params.profileIds || []).map(id => this.normalizeGuid(id)).filter(Boolean) as string[]));
    if (!uniqueIds.length) {
      return of([]);
    }

    const chunks = this.chunkArray(uniqueIds, 10);
    const requests = chunks.map(ids => this.fetchTasksChunk(ids, params));

    return forkJoin(requests).pipe(
      map(chunksResult => {
        const merged = chunksResult.flat();
        const deduped = Array.from(new Map(merged.map(task => [task.activityid, task])).values());
        return this.sortTasks(deduped, params.sortOrder || 'desc');
      }),
      catchError(() => of([]))
    );
  }

  createTask(payload: dynamicCRMModels.ICreateTaskPayload): Observable<any> {
    const url = `${Constant.config.dynamicCrmBaseUrl}api/data/v9.2/tasks`;
    console.debug('[DynamicCrmService] createTask request', {
      payload
    });

    return this.getCrmHttpOptions().pipe(
      switchMap(options => this._httpClient.post<any>(url, payload, options)),
      tap(response => {
        console.debug('[DynamicCrmService] createTask success', {
          activityid: response?.activityid,
          response
        });
      }),
      catchError(error => {
        console.error('[DynamicCrmService] createTask failed', {
          payload,
          error
        });
        return throwError(() => error);
      })
    );
  }

  uploadTaskAttachment(taskId: string, file: File): Observable<any> {
    const normalizedTaskId = this.normalizeGuid(taskId);
    if (!normalizedTaskId) {
      return of(null);
    }

    const encodedFileName = encodeURIComponent(file.name);
    const url = `${Constant.config.dynamicCrmBaseUrl}api/data/v9.2/tasks(${normalizedTaskId})/rcm_attachment?x-ms-file-name=${encodedFileName}`;
    return this.getCrmBinaryHttpOptions().pipe(
      switchMap(options => this._httpClient.patch(url, file, options))
    );
  }

  downloadTaskAttachment(taskId: string): Observable<Uint8Array | null> {
    const normalizedTaskId = this.normalizeGuid(taskId);
    if (!normalizedTaskId) {
      return of(null);
    }

    const url = `${Constant.config.dynamicCrmBaseUrl}api/data/v9.2/tasks(${normalizedTaskId})/rcm_attachment`;
    return this.getCrmReadHttpOptions().pipe(
      switchMap(options => this._httpClient.get<dynamicCRMModels.InteractionAttachment>(url, options)),
      map(response => {
        const base64 = response?.value;
        if (!base64) {
          return null;
        }

        const binary = atob(base64);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) {
          bytes[i] = binary.charCodeAt(i);
        }
        return bytes;
      }),
      catchError(() => of(null))
    );
  }

  closeTask(taskId: string): Observable<any> {
    const normalizedTaskId = this.normalizeGuid(taskId);
    if (!normalizedTaskId) {
      return of(null);
    }

    const url = `${Constant.config.dynamicCrmBaseUrl}api/data/v9.2/tasks(${normalizedTaskId})`;
    const payload = { statecode: 1, statuscode: dynamicCRMModels.INDIVIDUAL_TASK_STATUS.COMPLETED };
    return this.getCrmHttpOptions().pipe(
      switchMap(options => this._httpClient.patch(url, payload, options))
    );
  }

  sortTasks(tasks: dynamicCRMModels.ICrmTask[], sortOrder: dynamicCRMModels.TaskSortOrder): dynamicCRMModels.ICrmTask[] {
    const order = sortOrder === 'asc' ? 1 : -1;
    return [...tasks].sort((a, b) => {
      const aDate = a.createdon ? new Date(a.createdon).getTime() : 0;
      const bDate = b.createdon ? new Date(b.createdon).getTime() : 0;
      return (aDate - bDate) * order;
    });
  }

  // ── Interactions ──────────────────────────────────────────────────────────

  getInteractionsByProfiles(params: dynamicCRMModels.IInteractionQueryParams): Observable<dynamicCRMModels.ICrmInteraction[]> {
    const uniqueIds = Array.from(new Set((params.profileIds || []).map(id => this.normalizeGuid(id)).filter(Boolean) as string[]));
    if (!uniqueIds.length) {
      return of([]);
    }

    const chunks = this.chunkArray(uniqueIds, 20);
    const requests = chunks.map(ids => this.fetchInteractionsChunk(ids, params));

    return forkJoin(requests).pipe(
      map(chunksResult => {
        const merged = chunksResult.flat();
        const deduped = Array.from(new Map(merged.map(interaction => [interaction.activityid, interaction])).values());
        return this.sortInteractions(deduped, params.sortOrder || 'desc');
      }),
      catchError(() => of([]))
    );
  }

  addInteractionToContact(payload: dynamicCRMModels.ICreateInteractionPayload, attachment?: File): Observable<any> {
    const url = `${Constant.config.dynamicCrmBaseUrl}api/data/v9.2/aka_meetingnotes`;
    console.debug('[DynamicCrmService] addInteractionToContact request', {
      payload,
      hasAttachment: !!attachment,
      attachmentName: attachment?.name,
      attachmentSize: attachment?.size
    });

    return this.getCrmHttpOptions().pipe(
      switchMap(options => this._httpClient.post<any>(url, payload, options)),
      tap(response => {
        console.debug('[DynamicCrmService] addInteractionToContact create success', {
          activityid: response?.activityid,
          response
        });
      }),
      switchMap(response => {
        const interactionId = (response?.aka_meetingnoteid || response?.activityid) as string | undefined;
        if (!interactionId || !attachment) {
          return of(response);
        }
        return this.uploadInteractionAttachment(interactionId, attachment).pipe(
          tap(() => {
            console.debug('[DynamicCrmService] addInteractionToContact attachment upload success', {
              activityid: interactionId,
              attachmentName: attachment.name
            });
          }),
          map(() => response),
          catchError(error => {
            console.error('[DynamicCrmService] addInteractionToContact attachment upload failed', {
              activityid: interactionId,
              attachmentName: attachment.name,
              error
            });
            return of(response);
          })
        );
      }),
      catchError(error => {
        console.error('[DynamicCrmService] addInteractionToContact failed', {
          payload,
          hasAttachment: !!attachment,
          error
        });
        return throwError(() => error);
      })
    );
  }

  updateInteraction(interactionId: string, payload: dynamicCRMModels.ICreateInteractionPayload): Observable<any> {
    const normalizedInteractionId = this.normalizeGuid(interactionId);
    if (!normalizedInteractionId) {
      return of(null);
    }

    const url = `${Constant.config.dynamicCrmBaseUrl}api/data/v9.2/aka_meetingnotes(${normalizedInteractionId})`;
    return this.getCrmHttpOptions().pipe(
      switchMap(options => this._httpClient.patch<any>(url, payload, options))
    );
  }

  uploadInteractionAttachment(interactionId: string, file: File): Observable<any> {
    const normalizedInteractionId = this.normalizeGuid(interactionId);
    if (!normalizedInteractionId) {
      return of(null);
    }

    const url = `${Constant.config.dynamicCrmBaseUrl}api/data/v9.2/aka_meetingnotes(${normalizedInteractionId})/rcm_attachment`;
    const encodedFileName = encodeURIComponent(file.name);
    const uploadUrl = `${url}?x-ms-file-name=${encodedFileName}`;
    return this.getCrmBinaryHttpOptions().pipe(
      switchMap(options => {
        const headers = options.headers
          .set('Content-Type', 'application/octet-stream')
          .set('x-ms-file-name', file.name);

        const requestWithFileName = this._httpClient.patch(uploadUrl, file, { headers, context: options.context });
        const fallbackRequest = this._httpClient.patch(url, file, { headers, context: options.context });

        return requestWithFileName.pipe(
          catchError(err => {
            console.warn('[DynamicCrmService] uploadInteractionAttachment retrying without filename query.', {
              interactionId: normalizedInteractionId,
              fileName: file.name,
              error: err
            });
            return fallbackRequest;
          })
        );
      })
    );
  }

  downloadInteractionAttachment(interactionId: string): Observable<Uint8Array | null> {
    const normalizedInteractionId = this.normalizeGuid(interactionId);
    if (!normalizedInteractionId) {
      return of(null);
    }

    const url = `${Constant.config.dynamicCrmBaseUrl}api/data/v9.2/aka_meetingnotes(${normalizedInteractionId})/rcm_attachment`;
    return this.getCrmReadHttpOptions().pipe(
      switchMap(options => this._httpClient.get<dynamicCRMModels.InteractionAttachment>(url, options)),
      map(response => {
        const base64 = response?.value;
        if (!base64) {
          return null;
        }
        const binary = atob(base64);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) {
          bytes[i] = binary.charCodeAt(i);
        }
        return bytes;
      }),
      catchError(() => of(null))
    );
  }

  sortInteractions(interactions: dynamicCRMModels.ICrmInteraction[], sortOrder: dynamicCRMModels.InteractionSortOrder): dynamicCRMModels.ICrmInteraction[] {
    const order = sortOrder === 'asc' ? 1 : -1;
    return [...interactions].sort((a, b) => {
      const aDate = a.createdon ? new Date(a.createdon).getTime() : 0;
      const bDate = b.createdon ? new Date(b.createdon).getTime() : 0;
      return (aDate - bDate) * order;
    });
  }

  private fetchTasksChunk(
    profileIds: string[],
    params: dynamicCRMModels.ITaskQueryParams
  ): Observable<dynamicCRMModels.ICrmTask[]> {
    const profileFilter = profileIds.map(id => `_regardingobjectid_value eq '${id}'`).join(' or ');
    const activityFilter = `(activitytypecode eq 'task')`;
    const timeRangeFilter = this.getTaskTimeRangeFilter(params.timeRange || 'all');
    const textFilter = this.getContainsFilter(params.searchText, ['subject', 'description']);

    const filterParts = [`(${profileFilter})`, activityFilter];
    if (timeRangeFilter) {
      filterParts.push(`(${timeRangeFilter})`);
    }
    if (textFilter) {
      filterParts.push(`(${textFilter})`);
    }

    const select = [
      'activityid',
      'subject',
      'description',
      'scheduledend',
      'prioritycode',
      'statecode',
      'statuscode',
      'activitytypecode',
      'createdon',
      'modifiedon',
      '_ownerid_value',
      '_regardingobjectid_value'
    ].join(',');

    const sortOrder = params.sortOrder || 'desc';
    const url = `${Constant.config.dynamicCrmBaseUrl}api/data/v9.2/activitypointers?$select=${select}&$filter=${filterParts.join(' and ')}&$orderby=createdon ${sortOrder}`;

    return this.getCrmReadHttpOptions().pipe(
      switchMap(options => this._httpClient.get<{ value: any[] }>(url, options)),
      map(response => (response?.value || []).map(item => this.mapToICrmTask(item))),
      switchMap(tasks => {
        const taskIds = tasks
          .map(task => this.normalizeGuid(task.activityid))
          .filter((id): id is string => !!id);

        if (!taskIds.length) {
          return of(tasks);
        }

        return this.fetchTaskAttachmentNames(taskIds).pipe(
          map(attachmentMap => tasks.map(task => {
            const normalizedId = this.normalizeGuid(task.activityid) || '';
            return {
              ...task,
              filename: attachmentMap.get(normalizedId) || ''
            };
          })),
          catchError(() => of(tasks))
        );
      }),
      catchError(() => of([]))
    );
  }

  private fetchTaskAttachmentNames(taskIds: string[]): Observable<Map<string, string>> {
    const normalizedIds = Array.from(new Set(taskIds.map(id => this.normalizeGuid(id)).filter(Boolean) as string[]));
    if (!normalizedIds.length) {
      return of(new Map<string, string>());
    }

    const filter = normalizedIds.map(id => `activityid eq ${id}`).join(' or ');
    const url = `${Constant.config.dynamicCrmBaseUrl}api/data/v9.2/tasks?$select=activityid,rcm_attachment_name&$filter=${filter}`;

    return this.getCrmReadHttpOptions().pipe(
      switchMap(options => this._httpClient.get<{ value: any[] }>(url, options)),
      map(response => {
        const mapById = new Map<string, string>();
        for (const item of response?.value || []) {
          const normalizedId = this.normalizeGuid(item['activityid']);
          if (!normalizedId) {
            continue;
          }
          mapById.set(normalizedId, item['rcm_attachment_name'] || '');
        }
        return mapById;
      }),
      catchError(() => of(new Map<string, string>()))
    );
  }

  private fetchInteractionsChunk(
    profileIds: string[],
    params: dynamicCRMModels.IInteractionQueryParams
  ): Observable<dynamicCRMModels.ICrmInteraction[]> {
    const profileFilter = profileIds.map(id => `_regardingobjectid_value eq '${id}'`).join(' or ');
    let filter = `(${profileFilter})`;

    if (params.typeFilter && params.typeFilter !== 'all') {
      filter += ` and aka_type eq ${params.typeFilter}`;
    }

    const textFilter = this.getContainsFilter(params.searchText, ['subject', 'description']);
    if (textFilter) {
      filter += ` and (${textFilter})`;
    }

    const expand = 'regardingobjectid_contact($select=fullname)';
    const sortOrder = params.sortOrder || 'desc';
    const url = `${Constant.config.dynamicCrmBaseUrl}api/data/v9.2/aka_meetingnotes?$filter=${filter}&$expand=${expand}&$orderby=createdon ${sortOrder}`;

    return this.getCrmReadHttpOptions().pipe(
      switchMap(options => this._httpClient.get<{ value: any[] }>(url, options)),
      map(response => (response?.value || []).map(item => this.mapToICrmInteraction(item))),
      catchError(() => of([]))
    );
  }

  private getTaskTimeRangeFilter(timeRange: dynamicCRMModels.TaskTimeRange): string {
    const nowIso = new Date().toISOString();
    const sevenDays = new Date();
    sevenDays.setDate(sevenDays.getDate() + 7);
    const sevenDaysIso = sevenDays.toISOString();

    if (timeRange === 'immediate') {
      return `scheduledend ge ${nowIso} and scheduledend le ${sevenDaysIso}`;
    }

    if (timeRange === 'future') {
      return `scheduledend ge ${nowIso}`;
    }

    if (timeRange === 'past') {
      return `scheduledend lt ${nowIso}`;
    }

    return '';
  }

  private getContainsFilter(searchText: string | undefined, fields: string[]): string {
    const safe = this.escapeOdataString(searchText || '');
    if (!safe) {
      return '';
    }
    return fields.map(field => `contains(${field},'${safe}')`).join(' or ');
  }

  private mapToICrmTask(item: any): dynamicCRMModels.ICrmTask {
    return {
      activityid: item['activityid'],
      subject: item['subject'] || '',
      description: item['description'] || '',
      scheduledend: item['scheduledend'],
      prioritycode: item['prioritycode'],
      statecode: item['statecode'],
      statuscode: item['statuscode'],
      activitytypecode: item['activitytypecode'],
      createdon: item['createdon'],
      modifiedon: item['modifiedon'],
      ownerName: item['_ownerid_value@OData.Community.Display.V1.FormattedValue'] || '',
      contactFullName: item['_regardingobjectid_value@OData.Community.Display.V1.FormattedValue'] || '',
      accountName: item['_regardingobjectid_value@OData.Community.Display.V1.FormattedValue'] || '',
      objectId: item['_regardingobjectid_value'] || ''
    };
  }

  private mapToICrmInteraction(item: any): dynamicCRMModels.ICrmInteraction {
    return {
      activityid: item['activityid'],
      subject: item['subject'] || '',
      description: item['description'] || '',
      scheduledend: item['scheduledend'],
      createdon: item['createdon'],
      typeLabel: item['aka_type@OData.Community.Display.V1.FormattedValue'] || '',
      typeCode: item['aka_type'],
      regardingObjectId: item['_regardingobjectid_value'] || '',
      regardingName: item['_regardingobjectid_value@OData.Community.Display.V1.FormattedValue'] || item['regardingobjectid_contact']?.['fullname'] || '',
      city: item['rcm_city'] || '',
      stateProvince: item['rcm_stateprovince'] || '',
      countryRegion: item['rcm_countryregion'] || '',
      sentimentCode: item['ram_sentiment'],
      sentimentLabel: item['ram_sentiment@OData.Community.Display.V1.FormattedValue'] || '',
      meetingPurpose: item['rcm_meetingpurpose'] || '',
      attachmentName: item['rcm_attachment_name'] || '',
      serviceTypeCode: item['rcm_financialreporting'],
      reportTypeCodes: item['rcm_reporttype'],
      reportCoveredFromDate: item['rcm_reportcoveredfromdate'],
      reportCoveredToDate: item['rcm_reportcoveredtodate'],
      ownerName: item['_ownerid_value@OData.Community.Display.V1.FormattedValue'] || '',
      modifiedOn: item['modifiedon']
    };
  }

  private chunkArray<T>(items: T[], chunkSize: number): T[][] {
    const chunks: T[][] = [];
    for (let i = 0; i < items.length; i += chunkSize) {
      chunks.push(items.slice(i, i + chunkSize));
    }
    return chunks;
  }

  private normalizeGuid(value: string | null | undefined): string | null {
    if (!value) {
      return null;
    }
    return value.replace(/[{}]/g, '').trim() || null;
  }

  private escapeOdataString(value: string): string {
    return value.trim().replace(/'/g, "''");
  }
}
